import type { FC, ReactNode } from "react";
import { ACTION_ITEM_LABEL, type ActionItem } from "../../../../libs/mypage/actionItems";
import { normalizeOrderCode } from "../../../../libs/mypage/orderCode";
import { formatYen, type OrderDetailDTO } from "../../components/format";
import { LinkButton } from "../../components/LinkButton";

/**
 * 注文詳細の最上部に置く「ご対応のお願い」。開いてすぐに何をすればよいかが分かるよう、お願いしている
 * ことを 1 行ずつ、操作のボタン付きで並べる。判定は一覧と同じ `actionItems` を読む。
 *
 * 説明の詳しいところ (支払い方法ごとの案内・住所不備の理由・返品の状況) は各欄に残し、ここは
 * 「何をするか」と「どこでするか」だけに絞る。
 */
export const ActionItemsSection: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  if (order.actionItems.length === 0) return null;
  return (
    <section
      aria-label="ご対応のお願い"
      className="mb-10 rounded border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <h2 className="mb-1 font-medium text-amber-900 text-sm">ご対応のお願い</h2>
      <ul className="divide-y divide-amber-200/70">
        {order.actionItems.map((item) => (
          <li key={item.kind} className="py-3">
            <ActionItemRow item={item} order={order} />
          </li>
        ))}
      </ul>
    </section>
  );
};

const ActionItemRow: FC<{ item: ActionItem; order: OrderDetailDTO }> = ({ item, order }) => {
  const { description, note, action } = contentFor(item, order);
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0 text-amber-900 text-xs leading-relaxed">
        <p className="mb-0.5 font-medium text-sm">{ACTION_ITEM_LABEL[item.kind]}</p>
        <p>{description}</p>
        {note && <p className="mt-1 text-[11px] text-amber-900/80">{note}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

const externalButton = (href: string, label: string) => (
  <LinkButton href={href} target="_blank" rel="noreferrer" size="sm">
    {label}
  </LinkButton>
);

/** 返品カードのボタンと同じ名前にする (同じリンクを違う名前で出すと別の場所に見える) */
const returnPageButton = (url: string | null) =>
  url ? externalButton(url, "申請の詳細を確認する") : null;

type Content = { description: string; note?: string; action: ReactNode | null };

const PAYMENT_DEADLINE = "ご注文日から 7 日を過ぎますと、ご注文は自動的にキャンセルとなります。";

/** 支払い前の注文の案内。支払い方法によって、お客様が困っていることと再手続きの結果が違う */
const paymentContent = (
  gateway: NonNullable<OrderDetailDTO["additionalPaymentInfo"]>["gateway"] | undefined,
): Pick<Content, "description" | "note"> => {
  switch (gateway) {
    case "convenience_store":
      return {
        description: `お支払いが完了していません。振込番号がわからない・払込票を紛失された場合も、こちらから改めてお手続きいただけます。${PAYMENT_DEADLINE}`,
        note: "※ 新しい振込番号が発行され、以前の番号は無効になります。",
      };
    case "bank_transfer":
      return {
        description: `お支払いが完了していません。振込先がわからない場合や、お支払い方法を変更したい場合も、こちらから改めてお手続きいただけます。${PAYMENT_DEADLINE}`,
        note: "※ 新しいお支払い情報が発行され、以前のご案内は無効になります。",
      };
    default:
      return { description: `お支払いが完了していません。${PAYMENT_DEADLINE}` };
  }
};

const contentFor = (item: ActionItem, order: OrderDetailDTO): Content => {
  switch (item.kind) {
    case "payment":
      return {
        ...paymentContent(order.additionalPaymentInfo?.gateway),
        action: item.collectionUrl
          ? externalButton(item.collectionUrl, "お支払い手続きをする")
          : null,
      };
    case "additional_payment":
      return {
        description: `ご注文内容の変更に伴い、差額 ${formatYen(item.amountJpy)} のお支払いが必要です。お支払いを確認できましたら、発送の準備を進めます。`,
        action: externalButton(item.collectionUrl, "差額を支払う"),
      };
    case "address_fix":
      return {
        description:
          "ご登録のお届け先のご住所を確認できなかったため、発送を一時保留しております。郵便番号とご住所が一致しない、または番地が不足している可能性がございます。お届け先をご確認・ご修正ください。",
        action: order.isAddressEditable ? (
          <LinkButton to={`/orders/${normalizeOrderCode(order.code)}/address`} size="sm">
            お届け先を修正する
          </LinkButton>
        ) : null,
      };
    case "return_shipment":
      return {
        description: "ご返送する商品・返送先・返送方法は、申請の詳細からご確認ください。",
        note: "※ ご返送済みの場合、弊社で受取を確認するまでこの表示は変わりません。発送からしばらくお時間をいただく場合があります。",
        action: returnPageButton(item.customerPageUrl),
      };
    case "return_bank_account":
      return {
        description: "ご返金先の銀行口座を、申請の詳細からご入力ください。",
        action: returnPageButton(item.customerPageUrl),
      };
    case "return_handling_fee":
      return {
        description: "事務手数料のお支払いを、申請の詳細からお願いいたします。",
        action: returnPageButton(item.customerPageUrl),
      };
  }
};
